/* istanbul ignore file */

import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';
import type { EventsMap } from '@noths/polaris-client-next-redux';
import { selectIsFavourite, updateFavourite } from '@noths/polaris-client-user-favourites';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getPageTrackingContext } from 'src/tracking/utils/getPageTrackingContext';
import { getProductFieldObject } from 'src/utils/googleAnalytics';
import type { FavouriteEventLabel, FavouriteProductEvent } from './events';

const getEventLabel = (
  isOpeningModal: boolean,
  isExistingFavourite: boolean,
): FavouriteEventLabel => {
  if (isOpeningModal) {
    return 'folder options opened';
  }

  if (isExistingFavourite) {
    return 'removed';
  }

  return 'added';
};

type Action = ReturnType<(typeof updateFavourite)['fulfilled']>;

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [updateFavourite.fulfilled.type]: (state, _, action) => {
    const { payload: productCode } = action as Action;
    const { favourites, products } = state;
    const { lastPageLoaded, pages, productsReturnedCount } = products;
    const { isModalOpen, summary } = favourites;
    const isFavourite = selectIsFavourite(summary, productCode);

    if (!lastPageLoaded) {
      return;
    }

    const context = getPageTrackingContext(state);
    const favouritedProduct = pages[lastPageLoaded].find((prod) => prod.code === productCode)!;

    // Defer off the interaction's critical path: fire-and-forget, runs alongside the favourite re-render.
    deferTracking(() =>
      sendGAEvent<FavouriteProductEvent>({
        event: 'custom_event',
        event_category: 'Favourites',
        event_action: 'heart icon clicked',
        event_label: getEventLabel(isModalOpen, isFavourite),
        favourites: getProductFieldObject(favouritedProduct, pages, productsReturnedCount, context),
      }),
    );
  },
};
